import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Briefcase, CalendarHeart, Building2, RefreshCw, ArrowRight, Zap, Clock3 } from 'lucide-react';

/* Catering Growth — the event pipeline run like a sales CRM. */

const Grow = ({ children, i = 0, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y: 18 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-50px' }}
    transition={{ duration: 0.5, delay: i * 0.1 }}
    className={className}
  >
    {children}
  </motion.div>
);

export default function CateringPipelineShowcase({ lang }) {
  const es = lang === 'es';

  const stages = [
    {
      label: es ? 'Leads calificados' : 'Qualified leads',
      value: '8',
      tone: 'text-sky-300 border-sky-400/25 bg-sky-400/10',
      cards: [
        { n: es ? 'Tech Corp · 120 pax' : 'Tech Corp · 120 ppl', d: es ? 'Comida trimestral' : 'Quarterly lunch' },
        { n: es ? 'Ley & Asociados · 45 pax' : 'Law firm · 45 ppl', d: es ? 'Junta mensual' : 'Monthly board' },
      ],
    },
    {
      label: es ? 'Cotizados' : 'Quoted',
      value: '$24,300',
      tone: 'text-[#FFB672] border-[#F77F11]/30 bg-[#F77F11]/10',
      cards: [
        { n: es ? 'Boda García · 80 pax' : 'García wedding · 80 ppl', d: '$6,200' },
        { n: es ? 'Univ. Stanford · 60 pax' : 'Stanford Univ · 60 ppl', d: '$3,900' },
      ],
    },
    {
      label: es ? 'Confirmados' : 'Confirmed',
      value: es ? '3 eventos' : '3 events',
      tone: 'text-emerald-300 border-emerald-400/25 bg-emerald-400/10',
      cards: [
        { n: es ? 'Startup demo day · 150 pax' : 'Startup demo day · 150 ppl', d: es ? 'Sáb 12 · $9,800' : 'Sat 12 · $9,800' },
        { n: es ? 'Cumpleaños 50 · 35 pax' : '50th birthday · 35 ppl', d: es ? 'Dom 20 · $2,400' : 'Sun 20 · $2,400' },
      ],
    },
    {
      label: es ? 'Recompra' : 'Repeat',
      value: '2.3×',
      tone: 'text-violet-300 border-violet-400/25 bg-violet-400/10',
      cards: [
        { n: 'Tech Corp', d: es ? '4º pedido este año' : '4th order this year' },
        { n: es ? 'Colegio del Valle' : 'Valley School', d: es ? 'Pedido mensual fijo' : 'Standing monthly order' },
      ],
    },
  ];

  return (
    <section className="py-20 lg:py-24 bg-neutral-950 text-white relative overflow-hidden" data-testid="showcase-catering-growth">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-32 left-[15%] w-[600px] h-[420px] rounded-full blur-3xl opacity-[0.13]"
        style={{ background: 'radial-gradient(circle, #F77F11 0%, transparent 65%)' }}
      />
      <div className="relative max-w-[1180px] mx-auto px-6 lg:px-10">
        <div className="max-w-2xl">
          <p className="kx-eyebrow-dark">{es ? 'Tu pipeline en vivo' : 'Your live pipeline'}</p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-[-0.02em] leading-tight">
            {es ? 'El catering deja de ser suerte. Se vuelve pipeline.' : 'Catering stops being luck. It becomes pipeline.'}
          </h2>
          <p className="mt-4 text-neutral-400 text-[15.5px] leading-relaxed">
            {es
              ? 'La mayoría de restaurantes espera a que el catering "llegue". Nosotros lo operamos como un equipo de ventas: leads, cotizaciones, cierres y recompra — visibles cada semana.'
              : 'Most restaurants wait for catering to "show up". We run it like a sales team: leads, quotes, closes and reorders — visible every week.'}
          </p>
        </div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {stages.map((st, i) => (
            <Grow key={st.label} i={i}>
              <div className="h-full rounded-2xl bg-white/[0.04] border border-white/10 p-5 hover:border-[#F77F11]/40 transition-colors">
                <div className={`inline-flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-[11px] font-bold ${st.tone}`}>
                  {st.label}
                </div>
                <p className="mt-3 text-2xl font-bold tracking-[-0.01em]">{st.value}</p>
                <div className="mt-4 space-y-2.5">
                  {st.cards.map((cd) => (
                    <div key={cd.n} className="rounded-xl bg-black/25 border border-white/10 px-3.5 py-2.5">
                      <p className="text-[12.5px] font-semibold text-neutral-200 truncate">{cd.n}</p>
                      <p className="text-[11px] text-neutral-500">{cd.d}</p>
                    </div>
                  ))}
                </div>
                {i < stages.length - 1 && (
                  <ArrowRight className="hidden lg:block mt-4 w-4 h-4 text-neutral-600 ml-auto" aria-hidden="true" />
                )}
              </div>
            </Grow>
          ))}
        </div>

        <Grow i={2}>
          <div className="mt-6 grid sm:grid-cols-2 gap-3">
            <div className="flex items-start gap-2.5 rounded-xl bg-emerald-400/10 border border-emerald-400/25 px-4 py-3">
              <Zap className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
              <p className="text-[12px] text-neutral-200 leading-relaxed">
                {es
                  ? 'Insight: los compradores corporativos que repiten en 60 días valen 4× más. Dos cuentas están listas para follow-up.'
                  : 'Insight: corporate buyers who reorder within 60 days are worth 4× more. Two accounts are ready for follow-up.'}
              </p>
            </div>
            <div className="flex items-start gap-2.5 rounded-xl bg-[#F77F11]/10 border border-[#F77F11]/25 px-4 py-3">
              <Clock3 className="w-4 h-4 text-[#FFB672] shrink-0 mt-0.5" />
              <p className="text-[12px] text-neutral-200 leading-relaxed">
                {es
                  ? 'Cotización de Tech Corp sin respuesta hace 5 días — follow-up redactado y listo para enviar.'
                  : 'Tech Corp quote unanswered for 5 days — follow-up drafted and ready to send.'}
              </p>
            </div>
          </div>
          <p className="mt-6 text-[13.5px] text-neutral-400">
            {es ? '¿Quieres que la IA califique y contacte a los leads por ti? Conoce ' : 'Want AI to score and reach out to leads for you? Meet '}
            <Link to="/ai/catering-ai" className="text-[#FFB672] hover:text-white font-semibold underline underline-offset-4 decoration-[#F77F11]/50" data-testid="catering-showcase-link-ai">
              Catering AI™
            </Link>
            {es ? ' — la capa de IA de este programa.' : ' — the AI layer of this program.'}
          </p>
        </Grow>
      </div>
    </section>
  );
}
